/**
 * @fileoverview Optimization Polling Hook
 * 
 * Manages polling logic for optimization job updates.
 * Smart polling: only polls when there are active jobs.
 */

import { useEffect, useRef, useCallback } from 'react';

export interface UseOptimizationPollingOptions {
  /** Polling interval in milliseconds */
  pollInterval: number;
  /** Whether polling should be active */
  shouldPoll: boolean;
  /** Whether there are active/queued jobs */
  hasActiveJobs: boolean;
  /** Function to call on each poll */
  onPoll: () => void;
}

export interface UseOptimizationPollingReturn {
  /** Manually trigger a poll */
  triggerPoll: () => void;
  /** Start polling */
  startPolling: () => void;
  /** Stop polling */
  stopPolling: () => void;
}

/**
 * Hook for managing smart polling for optimization jobs
 */
export function useOptimizationPolling(
  options: UseOptimizationPollingOptions
): UseOptimizationPollingReturn {
  const { pollInterval, shouldPoll, hasActiveJobs, onPoll } = options;
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearIntervals = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  const startPollingInternal = useCallback(() => {
    if (!intervalRef.current && hasActiveJobs) {
      intervalRef.current = setInterval(onPoll, pollInterval);
    }
  }, [hasActiveJobs, onPoll, pollInterval]);

  const stopPollingInternal = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Effect to manage polling based on shouldPoll and hasActiveJobs
  useEffect(() => {
    if (!shouldPoll || !hasActiveJobs) {
      clearIntervals();
      return;
    }

    // Start polling
    startPollingInternal();

    // Periodic check to ensure polling is running when needed
    checkIntervalRef.current = setInterval(() => {
      if (hasActiveJobs && !intervalRef.current) {
        startPollingInternal();
      } else if (!hasActiveJobs && intervalRef.current) {
        stopPollingInternal();
      }
    }, 2000);

    return clearIntervals;
  }, [shouldPoll, hasActiveJobs, clearIntervals, startPollingInternal, stopPollingInternal]);

  const triggerPoll = useCallback(() => {
    onPoll();
  }, [onPoll]);

  return {
    triggerPoll,
    startPolling: startPollingInternal,
    stopPolling: stopPollingInternal,
  };
}
